import React, { useState, useEffect } from "react";
import api from "../api/client";
import BehaviorNav from "./BehaviorNav";

const RANGES = ["7d", "30d", "90d"];

function ExitRateBadge({ rate }) {
  if (rate == null) return <span className="text-gray-500">—</span>;
  let cls = "text-green-400 bg-green-900/20";
  if (rate > 30) cls = "text-red-400 bg-red-900/20";
  else if (rate > 15) cls = "text-amber-400 bg-amber-900/20";
  return (
    <span className={`inline-block text-xs px-2 py-0.5 rounded font-medium ${cls}`}>
      {rate.toFixed(1)}%
    </span>
  );
}

export default function BehaviorExitIntent({ selectedSite }) {
  const [data, setData] = useState([]);
  const [range, setRange] = useState("7d");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!selectedSite) return;
    setLoading(true);
    api
      .get("/behavior/exit-intent/", {
        params: { website_id: selectedSite.id, range },
      })
      .then((res) => setData(res.data.results || res.data))
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, [selectedSite, range]);

  if (!selectedSite) {
    return (
      <div className="text-gray-500 text-center py-20">
        Select a website to view exit intent data
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Exit Intent</h2>
        <div className="flex bg-dark-300 border border-dark-50 rounded-lg p-0.5">
          {RANGES.map((r) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                range === r
                  ? "bg-copper/20 text-copper"
                  : "text-gray-400 hover:text-white"
              }`}
            >
              {r}
            </button>
          ))}
        </div>
      </div>

      <BehaviorNav />

      {loading ? (
        <div className="animate-pulse space-y-2">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-12 bg-dark-300 rounded-lg" />
          ))}
        </div>
      ) : (
        <div className="bg-dark-300 border border-dark-50 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500 border-b border-dark-50">
                  <th className="text-left py-2.5 px-4 font-medium">Page Path</th>
                  <th className="text-right py-2.5 px-3 font-medium">Exit Intents</th>
                  <th className="text-right py-2.5 px-3 font-medium">Total Views</th>
                  <th className="text-right py-2.5 px-3 font-medium">Exit Rate</th>
                  <th className="text-right py-2.5 px-3 font-medium">Avg Scroll at Exit</th>
                  <th className="text-right py-2.5 px-3 font-medium">Avg Time Before Exit</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row, i) => (
                  <tr
                    key={i}
                    className="border-b border-dark-50/50 hover:bg-dark-50/30"
                  >
                    <td className="py-2.5 px-4 font-mono text-xs text-gray-300 truncate max-w-xs">
                      {row.path}
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono text-gray-400">
                      {row.exit_intent_count?.toLocaleString()}
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono text-gray-400">
                      {row.total_pageviews?.toLocaleString()}
                    </td>
                    <td className="py-2.5 px-3 text-right">
                      <ExitRateBadge rate={row.exit_intent_rate} />
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono text-gray-400">
                      {row.avg_scroll_at_exit != null
                        ? `${row.avg_scroll_at_exit.toFixed(0)}%`
                        : "—"}
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono text-gray-400">
                      {row.avg_time_before_exit != null
                        ? `${row.avg_time_before_exit.toFixed(1)}s`
                        : "—"}
                    </td>
                  </tr>
                ))}
                {!data.length && (
                  <tr>
                    <td colSpan={6} className="py-12 text-center text-gray-500">
                      No exit intent data yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
